import { FC, Fragment, ReactNode, useRef, useState } from 'react'
import { BaseTimeFormat, COMPONENT_DEFAULT_ERRORS, USER_ROLES } from '@/data'
import { ImageOnError, ImageOnDrag } from '@/helpers'
import { useImage, useNotification } from '@/hooks'
import { Copy, Delete } from '@/controllers/component'

import {
    CategoryState,
    ComponentState,
    ConfirmationState,
    SideoverState,
    TagState,
    UserState
} from '@/states'

import {
    CalendarIcon,
    CurrencyDollarIcon,
    EyeIcon,
    PencilSquareIcon,
    PhotoIcon,
    RectangleGroupIcon,
    Square2StackIcon,
    TrashIcon,
    UserIcon
} from '@heroicons/react/16/solid'

const GridItem: FC<ComponentInterface> = (props): ReactNode => {
    const {
        loading: isLoading,
        setLoading: setIsLoading,
        error: isError,
        setError: setIsError
    } = useImage()

    const {
        loading: ImageLoading,
        setLoading: ImageSetIsLoading,
        error: ImageError,
        setError: ImageSetError
    } = useImage()

    const { InitNotification } = useNotification()
    const { User: Auth } = UserState()
    const { SetState: ConfirmationSetState } = ConfirmationState()
    const { SetState: SideoverSetState, Loading } = SideoverState()
    const { Tags: DynamicTags, Loading: TagsLoading } = TagState()
    const { Loading: CategoriesLoading } = CategoryState()

    const {
        SetState,
        Components,
        Deleting,
        Copying,
        Filters
    } = ComponentState()

    const HandleTags = (tags: Array<TagInterface>) => {
        const all_tags = DynamicTags
        const component_tags = tags

        const matched_tags = all_tags.filter((tag: TagInterface) => component_tags.some((componentTag: TagInterface) => componentTag._id === tag._id))

        const ids = matched_tags.map((tag: TagInterface) => tag._id)
        return ids
    }

    const {
        _id,
        Name,
        Category,
        Created_At,
        Image,
        Clipboard,
        Link,
        User,
        Tags,
        Price,
        Video
    } = props

    const Copier = () => Copying === false && Copy(
        SetState,
        // @ts-ignore
        _id,
        InitNotification
    )

    const Deletor = () => (Auth?._id === User?._id && Auth?.Role === USER_ROLES.ADMIN) && ConfirmationSetState({
        Title: `Are you sure you want to delete '${Name}' component?`,
        Open: true,
        onConfirm: () => {
            Delete(
                _id,
                Components,
                InitNotification,
                SetState
            )

            ConfirmationSetState({
                Open: false,
                Title: '',
                onClick: () => {}
            })
        }
    })

    const OpenEdit = () => {
        if (Auth?.Role === USER_ROLES.ADMIN) SideoverSetState({
            Loading: false,
            Open: true,
            Mode: 'edit',
            Errors: COMPONENT_DEFAULT_ERRORS,
            Target: 'component',
            Form: {
                _id,
                Name,
                Category: Category?._id,
                Tags: HandleTags(Tags),
                Link,
                Image,
                Clipboard,
                Price,
                Video
            }
        })
    }

    let CurrentUploadPreview: any = ''

    if (Video && ((Video as any) instanceof Blob || (Video as any) instanceof MediaSource)) {
        CurrentUploadPreview = URL.createObjectURL(Video as any)
    }

    else if (typeof Video === 'string' && Video.length !== 0) CurrentUploadPreview = Video

    const VideoReference = useRef(CurrentUploadPreview)

    const [isVideoPlaying, setIsVideoPlaying] = useState(false)

    const HandleSelect = (value: string, target: keyof ComponentFilterClientTypes) => {
        if (!Loading && !Loading && !CategoriesLoading && !TagsLoading && !Deleting) {
            const filters = structuredClone(Filters)
            filters[target] = value

            SetState({ Filters: filters })
        }
    }

    return (
        <article
            style={Deleting === _id ? { pointerEvents: 'none', userSelect: 'none' } : { pointerEvents: 'auto' }}
            className='bg-[#262626] select-none rounded-md border border-[#424242] relative z-0'
            onMouseEnter={() => {
                if (VideoReference.current) {
                    VideoReference.current.play()
                    setIsVideoPlaying(true)
                }
            }}
            onMouseLeave={() => {
                if (VideoReference.current) {
                    VideoReference.current.pause()
                    setIsVideoPlaying(false)
                }
            }}
        >
            {
                (Deleting === _id && Auth?.Role === USER_ROLES.ADMIN) &&
                <section className='w-full h-full absolute top-0 left-0 right-0 bottom-0 bg-[#404040] opacity-80 rounded-md flex items-center justify-center ignore-blur z-[150]'>
                    <svg className='w-7 h-7 text-[#EBEBEB] animate-spin fill-[#40404090] ignore-blur' viewBox='0 0 100 101' fill='none' xmlns='http://www.w3.org/2000/svg'>
                        <path className='ignore-blur' d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z' fill='currentColor'/>
                        <path className='ignore-blur' d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z' fill='currentFill'/>
                    </svg>
                </section>
            }

            {
                (Deleting === null && Auth?.Role === USER_ROLES.ADMIN) &&
                <div className='flex gap-1 absolute top-2 right-2'>
                    {
                        Auth?._id === User?._id &&
                        <div onClick={Deletor} className='w-7 h-7 bg-[#404040] hover:bg-[#5a5a5a] text-[#EBEBEB] hover:text-[#EBEBEB] rounded-md flex items-center justify-center cursor-pointer transition-all duration-500 ease-in-out z-[11]'>
                            <TrashIcon className='w-4 h-4' />
                        </div>
                    }

                    <div onClick={OpenEdit} className='w-7 h-7 bg-[#404040] hover:bg-[#5a5a5a] text-[#EBEBEB] hover:text-[#EBEBEB] rounded-md flex items-center justify-center cursor-pointer transition-all duration-500 ease-in-out z-[11]'>
                        <PencilSquareIcon className='w-4 h-4' />
                    </div>
                </div>
            }

            <div className='h-[132px] select-none w-full flex justify-center items-center object-cover rounded-t-md relative bg-[#1E1E1E]'>
                <video
                    className='w-full h-full absolute top-0 left-0 right-0 bottom-0 object-cover rounded-t-md z-10'
                    src={CurrentUploadPreview}
                    autoPlay
                    loop
                    muted
                    ref={VideoReference}
                />

                <img
                    className='w-full h-full absolute top-0 left-0 right-0 bottom-0 object-cover rounded-t-md z-10'
                    src={Image}
                    alt=''
                    title=''
                    onDragStart={ImageOnDrag}
                    onLoad={() => ImageSetIsLoading(false)}
                    style={isVideoPlaying || ImageLoading || ImageError ? { display: 'none' } : { display: 'flex' }}
                    onError={
                        (e) => {
                            ImageSetError(true)
                            ImageOnError(e)
                        }
                    }
                />

                <PhotoIcon className='w-10 h-10 mt-2 text-[#929699] animate-pulse' />
            </div>

            <div className='flex flex-col justify-between'>
                <div className='flex flex-col h-full'>
                    <div className='flex select-text p-1 bg-[#404040] mb-2 items-center text-[#CDD5DA]'>
                        {
                            Category &&
                            <span onClick={() => HandleSelect(Category?._id, 'Category')} className='text-[11px] font-medium inline-flex items-center px-1 py-0.5 rounded hover:bg-[#ffffff10] select-none duration-500 ease-in-out transition-all cursor-pointer'>
                                <RectangleGroupIcon className='w-4 h-4 text-[#CDD5DA] mr-1' />

                                {Category?.Name?.length >= 12 ? Category?.Name?.slice(0, 12)?.trim() + '...' : Category?.Name}
                            </span>
                        }

                        {
                            Created_At &&
                            <span className='select-text text-[11px] font-medium inline-flex items-center px-1 py-0.5 rounded'>
                                <CalendarIcon className='w-4 h-4 text-[#EBEBEB] mr-1' />

                                {BaseTimeFormat(Created_At).split(', ')[0]}
                            </span>
                        }

                        {
                            Price !== undefined &&
                            <span className='select-text text-[11px] font-medium inline-flex items-center px-1 py-0.5 rounded'>
                                <CurrencyDollarIcon className='w-4 h-4 text-[#EBEBEB] mr-1' />

                                {Price === 0 ? 'Free' : `$${Price}`}
                            </span>
                        }
                    </div>

                    <div className={`gap-2 flex flex-col pb-3 ${Auth?.Role === USER_ROLES.ADMIN ? 'border-b mb-2 border-[#424242]' : ''}`}>
                        <h2 className='px-2 select-text text-xl h-auto font-bold tracking-tight text-[#EBEBEB]'>
                            {Name?.length >= 42 ? Name?.slice(0, 42) + '...' : Name}
                        </h2>

                        <div className='gap-1.5 px-2 w-full flex rounded-md text-sm items-start justify-start flex-wrap'>
                            {
                                Tags?.length > 0 && Tags?.map((tag: TagInterface) => {
                                    return (
                                        <span
                                            key={tag._id}
                                            onClick={() => HandleSelect(tag._id, 'Tag')}
                                            className='flex capitalize items-center text-xs p-1 px-1.5 bg-[#4A4A4A] text-[#CDD5DA] rounded-md hover:opacity-90 transition-all duration-500 ease-in-out cursor-pointer'
                                        >
                                            {tag?.Name?.replace(',', '')}
                                        </span>
                                    )
                                })
                            }

                            {
                                Tags?.length === 0 &&
                                <Fragment>
                                    <span className='flex capitalize items-center text-xs p-1 px-1.5 bg-[#4A4A4A50] text-[#CDD5DA] rounded-md'>
                                        No Tags Added
                                    </span>
                                </Fragment>
                            }
                        </div>
                    </div>
                </div>

                {
                    Auth?.Role === USER_ROLES.ADMIN &&
                    <div className='flex items-center justify-between p-3 pb-2 pt-0 pr-3 h-full'>
                        <div className='flex items-center space-x-1.5'>
                            <div className='select-none flex items-center relative'>
                                <div className='button-gradient select-none flex items-center justify-center relative text-sm w-[26px] h-[26px] rounded-md'>
                                    <img
                                        className='w-[26px] h-[26px] p-[2px] border-0 outline-0 rounded-md absolute object-cover z-10'
                                        src={User?.Avatar}
                                        alt={User?.Name}
                                        referrerPolicy='no-referrer'
                                        style={(isLoading || isError) ? { display: 'none' } : { display: 'flex' }}
                                        onDragStart={ImageOnDrag}
                                        onLoad={() => setIsLoading(false)}
                                        onError={(e) => {
                                            setIsError(true)
                                            ImageOnError(e)
                                        }}
                                    />

                                    <UserIcon className='w-6 h-6 text-[#EBEBEB]' />
                                </div>
                            </div>

                            <div className='select-text flex flex-col'>
                                <span className='font-medium text-[14.5px] text-[#EBEBEB] capitalize'>
                                    {User === undefined && 'Unknown'}
                                    {User !== undefined && User?.Name?.split(' ')[0].length >= 10 ? User?.Name?.split(' ')[0].slice(0, 10) + '...' : User?.Name?.split(' ')[0]}
                                </span>
                            </div>
                        </div>

                        <div className='flex gap-1 items-center'>
                            {
                                Link !== '' &&
                                <a
                                    href={(Link?.includes('http://') || Link?.includes('https://')) ? Link : `https://${Link}`}
                                    target='_blank'
                                    rel='noreferrer'
                                    className='select-none flex bg-[#404040] items-center justify-center px-1.5 py-1.5 text-sm font-medium rounded-md hover:opacity-70 text-[#EBEBEB] transition-all ease-in-out duration-500'
                                >
                                    <EyeIcon className='flex-shrink-0 w-4 h-4 text-[#CDD5DA]' />
                                </a>
                            }

                            <button className='select-none flex button-gradient items-center justify-center w-full px-2 py-1 text-sm font-medium text-[#EBEBEB] rounded-md md:w-auto hover:opacity-90 transition-all ease-in-out duration-500'>
                                { Copying !== _id && <Square2StackIcon className='flex-shrink-0 w-4 h-4 text-[#EBEBEB]' /> }

                                {
                                    Copying === _id &&
                                    <section className='flex mr-0.5 items-center justify-center ignore-blur z-[150]'>
                                        <svg className='w-[14px] h-[14px] text-[#EBEBEB] animate-spin fill-[#40404090] ignore-blur' viewBox='0 0 100 101' fill='none' xmlns='http://www.w3.org/2000/svg'>
                                            <path className='ignore-blur' d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z' fill='currentColor'/>
                                            <path className='ignore-blur' d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z' fill='currentFill'/>
                                        </svg>
                                    </section>
                                }

                                <span onClick={Copier} className='text-[#EBEBEB] text-[13px] ml-1'>
                                    Copy
                                </span>
                            </button>
                        </div>
                    </div>
                }
            </div>
        </article>
    )
}

export default GridItem
